// ---------------------------------------------------------------------------
// extract-block-jsonld — Coogo block → minimal JSON-LD for per-block SHACL
// validation.
//
// Consumer of `docs/agentic/client-independence.md §2.2`: a block's
// `ui` / `flow` / `events` files aren't JSON-LD themselves, so this util
// produces a minimal `@graph` listing every semantic type the block
// references. `repo.loadJsonLd(extractBlockJsonLd(block))` then puts those
// types into a named scope graph that `validate('structural')` can inspect.
//
// Scope (v1): detect "does this block reference a type not declared in
// cogo-ns-v1?" — SHACL surfaces that as an unknown-type violation. Deeper
// binding / cardinality / property-value constraints are v2 agentic work
// (see `cogo-ns-v2-roadmap.md` §3).
//
// Mapping:
//   ui.root tree      → cogo:<type>  (e.g. cogo:button, cogo:container)
//   flow.*.steps[*]   → cogo:<action>  (basic / expression steps only)
//   events.bindings   → cogo:<event>  (parsed from "id:event" keys)
//
// Duplicates are removed: each unique type IRI appears once in @graph, with
// a stable `_:b<N>` blank-node id for traceability in violation messages.
// ---------------------------------------------------------------------------

import { foldBundleEventBindings } from "@cogo/publisher-core/event-binding-shape"

const COGO_NS = "https://cogo.xyz/ns/v1#"

export interface BlockJsonLd {
  "@context": { cogo: string }
  "@graph": Array<{ "@id": string; "@type": string }>
}

/**
 * Walk a Coogo block (ui / flow / events) and emit a minimal JSON-LD document
 * listing every unique semantic type referenced.
 *
 * Accepts `Record<string, unknown>` because the Studio's `LoadedBlock` shape
 * treats each file as a generic JSON object. Defensive against missing /
 * malformed sections — callers should never see an exception for a legal
 * partial block.
 */
export function extractBlockJsonLd(block: {
  ui?: unknown
  flow?: unknown
  events?: unknown
}): BlockJsonLd {
  const types = new Set<string>()

  collectComponentTypes(getRecord(block.ui), types)
  collectActionTypes(getRecord(block.flow), types)
  collectEventTypes(getRecord(block.events), types)

  const graph = [...types]
    .sort()
    .map((t, idx) => ({ "@id": `_:b${idx}`, "@type": `cogo:${t}` }))

  return {
    "@context": { cogo: COGO_NS },
    "@graph": graph,
  }
}

// --- Component types (ui.cogo.json) -----------------------------------------

function collectComponentTypes(
  ui: Record<string, unknown> | null,
  out: Set<string>,
): void {
  if (!ui) return
  const root = getRecord(ui.root)
  if (root) walkComponentNode(root, out)
}

function walkComponentNode(
  node: Record<string, unknown>,
  out: Set<string>,
): void {
  const type = node.type
  if (typeof type === "string" && type.length > 0) {
    out.add(sanitizeLocalName(type))
  }
  const children = node.children
  if (Array.isArray(children)) {
    for (const child of children) {
      const c = getRecord(child)
      if (c) walkComponentNode(c, out)
    }
  }
}

// --- Action types (flow.cogo.json) ------------------------------------------

function collectActionTypes(
  flowFile: Record<string, unknown> | null,
  out: Set<string>,
): void {
  if (!flowFile) return
  // flow.cogo.json shape: { flows: { <name>: { steps: [...] } } }  OR
  // legacy shape: { <name>: { steps: [...] } }  (no outer "flows")
  const flows = getRecord(flowFile.flows) ?? flowFile
  for (const flow of Object.values(flows)) {
    const flowObj = getRecord(flow)
    if (!flowObj) continue
    const steps = flowObj.steps
    if (Array.isArray(steps)) walkFlowSteps(steps, out)
  }
}

function walkFlowSteps(steps: unknown[], out: Set<string>): void {
  for (const raw of steps) {
    const step = getRecord(raw)
    if (!step) continue
    const stepType = step.type
    if (stepType === "basic" || stepType === "expression") {
      const action = step.action
      if (typeof action === "string" && action.length > 0) {
        out.add(sanitizeLocalName(action))
      }
    } else if (stepType === "conditional") {
      // Recurse into then/else branches.
      walkFlowBranch(step.then, out)
      walkFlowBranch(step.else, out)
    } else if (stepType === "loop") {
      // Canonical v4/runtime-v2 uses `steps`; retain `body` as a legacy fallback.
      walkFlowBranch(step.steps ?? step.body, out)
    } else if (stepType === "parallel") {
      if (Array.isArray(step.steps)) walkFlowSteps(step.steps, out)
    } else if (stepType === "tryCatch") {
      walkFlowBranch(step.try, out)
      walkFlowBranch(step.catch, out)
      walkFlowBranch(step.finally, out)
    } else if (stepType === "switch") {
      const cases = step.cases
      const caseMap = getRecord(cases)
      if (caseMap) {
        for (const caseSteps of Object.values(caseMap)) {
          walkFlowBranch(caseSteps, out)
        }
      } else if (Array.isArray(cases)) {
        // Legacy editor shape: [{ value, steps }].
        for (const caseRaw of cases) {
          const c = getRecord(caseRaw)
          if (c && Array.isArray(c.steps)) walkFlowSteps(c.steps, out)
        }
      }
      walkFlowBranch(step.default, out)
    } else if (stepType === "stage") {
      walkFlowBranch(step.onEnter, out)
      walkFlowBranch(step.steps, out)
      walkFlowBranch(step.onSuccess, out)
      walkFlowBranch(step.onError, out)
      walkFlowBranch(step.onFinally, out)
      walkFlowBranch(step.compensate, out)
    } else if (stepType === "saga") {
      walkFlowBranch(step.stages, out)
    }
  }
}

function walkFlowBranch(branch: unknown, out: Set<string>): void {
  if (Array.isArray(branch)) {
    walkFlowSteps(branch, out)
    return
  }
  const obj = getRecord(branch)
  if (obj && Array.isArray(obj.steps)) walkFlowSteps(obj.steps, out)
}

// --- Event types (events.cogo.json) -----------------------------------------

function collectEventTypes(
  events: Record<string, unknown> | null,
  out: Set<string>,
): void {
  if (!events) return
  // events.cogo.json accepts TWO equivalent spellings — the flat
  // `bindings: { "id:event": {...} }` map and the nested
  // `elements: { id: { event: {...} } }` map the agentic generator emits
  // 357/357 times (cogo-runtime-v2 app-runtime-core.ts:1617/:1699 read both;
  // render-pack.ts:415 reads ONLY `elements`). The previous
  // `getRecord(events.bindings) ?? events` fell back to the artifact itself,
  // whose only key is then `"elements"` — no colon, so ZERO event types were
  // collected from every generated block. Task 4 / authority-repair
  // (2026-08-09, review round 2).
  const bindings = foldBundleEventBindings(events)
  for (const key of Object.keys(bindings)) {
    const colonIdx = key.indexOf(":")
    if (colonIdx < 0 || colonIdx === key.length - 1) continue
    const eventName = key.slice(colonIdx + 1).trim()
    if (eventName.length > 0) out.add(sanitizeLocalName(eventName))
  }
}

// --- Helpers ----------------------------------------------------------------

function getRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>
  }
  return null
}

// Keep legal IRI local-name chars; strip anything that would break the JSON-LD
// or SPARQL downstream. Matches the namespace-local-name convention used in
// cogo-ns-v1.ttl — lowercase alphanumerics, underscore, hyphen.
function sanitizeLocalName(s: string): string {
  return s.replace(/[^A-Za-z0-9_\-.]/g, "")
}
